import { getAuthHeaders, getStoredUser } from "../lib/auth";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import LogLayout from "../components/LogLayout";
import SectionTabs, { INVOICES_TABS } from "../components/SectionTabs";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay, useDroppable, useDraggable,
} from "@dnd-kit/core";
import {
  Clipboard, Send, Banknote, FileText, Landmark, CheckCircle,
  Search, Filter, Truck, Users, ArrowRight, GripVertical,
  Package, Eye, UserPlus, Mail, RefreshCw, Loader2, X, AlertTriangle,
  Clock, LayoutGrid, Table2, FileDown, Paperclip
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const STAGES = [
  { key: "a_deposer", label: "À déposer", icon: Clipboard, color: "amber" },
  { key: "deposee", label: "Déposée", icon: Send, color: "blue" },
  { key: "cheque_a_recuperer", label: "Chèque à récupérer", icon: Banknote, color: "purple" },
  { key: "bordereau_a_recuperer", label: "Bordereau à récupérer", icon: FileText, color: "indigo" },
  { key: "remise_cheque", label: "Remise de chèque", icon: Landmark, color: "cyan" },
  { key: "encaisse", label: "Encaissé", icon: CheckCircle, color: "emerald" },
];

const COLOR_MAP = {
  amber: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", badge: "bg-amber-500", ring: "ring-amber-500/40" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30", badge: "bg-blue-500", ring: "ring-blue-500/40" },
  purple: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30", badge: "bg-purple-500", ring: "ring-purple-500/40" },
  indigo: { bg: "bg-indigo-500/10", text: "text-indigo-400", border: "border-indigo-500/30", badge: "bg-indigo-500", ring: "ring-indigo-500/40" },
  cyan: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/30", badge: "bg-cyan-500", ring: "ring-cyan-500/40" },
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", badge: "bg-emerald-500", ring: "ring-emerald-500/40" },
};

const MISSION_TYPES = [
  { value: "depot", label: "Dépôt de facture" },
  { value: "recuperation_cheque", label: "Récupération de chèque" },
  { value: "recuperation_bordereau", label: "Récupération de bordereau" },
  { value: "remise_cheque", label: "Remise de chèque à la banque" },
];

const formatFCFA = (v) => new Intl.NumberFormat("fr-FR").format(Math.round(v || 0)) + " F";

const INVOICE_STATUS_MAP = {
  editee: { label: "Éditée", color: "bg-slate-500/20 text-slate-400" },
  received: { label: "Éditée", color: "bg-slate-500/20 text-slate-400" },
  deposee: { label: "Déposée", color: "bg-blue-500/20 text-blue-400" },
  rejetee: { label: "Rejetée", color: "bg-red-500/20 text-red-400" },
  paiement_partiel: { label: "Paiement partiel", color: "bg-amber-500/20 text-amber-400" },
  paiement_total: { label: "Paiement total", color: "bg-emerald-500/20 text-emerald-400" },
  paiement_effectue: { label: "Payée", color: "bg-emerald-500/20 text-emerald-400" },
  archived: { label: "Archivée", color: "bg-slate-500/20 text-slate-400" },
};

/* ─── Draggable Invoice Card ─── */
function DraggableCard({ inv, onAssign, onLiasse, onJoin, isAdmin = true }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: inv.id, data: { stage: inv.pipeline_stage }, disabled: !isAdmin });
  const style = { transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 50 : "auto" };
  const photosCount = inv.photos_count ?? 0;
  const docType = inv.doc_type || "facture";
  const docTypeStyle = {
    facture: "bg-slate-700/60 text-slate-300",
    cheque: "bg-purple-500/20 text-purple-300",
    remise: "bg-cyan-500/20 text-cyan-300",
    depot: "bg-blue-500/20 text-blue-300",
  }[docType] || "bg-slate-700/60 text-slate-300";
  const docTypeLabel = {
    facture: "Facture",
    cheque: "Chèque",
    remise: "Remise",
    depot: "Dépôt",
  }[docType] || docType;
  return (
    <div ref={setNodeRef} style={style} className="bg-slate-950 border border-white/10 rounded-lg p-3 hover:border-cyan-500/30 transition group" data-testid={`card-${inv.id}`}>
      <div className="flex items-start gap-2">
        {isAdmin && (
          <button {...attributes} {...listeners} className="mt-0.5 text-slate-600 hover:text-slate-400 cursor-grab active:cursor-grabbing" data-testid={`drag-${inv.id}`}>
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-bold text-white truncate flex-1">{inv.invoice_number}</p>
            <span className={`text-[8px] px-1 py-0.5 rounded ${docTypeStyle} font-medium uppercase flex-shrink-0`} title={`Type : ${docTypeLabel}`}>
              {docTypeLabel}
            </span>
          </div>
          <p className="text-[10px] text-slate-500 truncate">{inv.pharmacy_name}</p>
          <p className="text-[10px] text-slate-600 truncate">{inv.insurer_name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-xs font-bold text-emerald-400">{formatFCFA(inv.amount)}</p>
            {inv.invoice_status && INVOICE_STATUS_MAP[inv.invoice_status] && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${INVOICE_STATUS_MAP[inv.invoice_status].color}`}>
                {INVOICE_STATUS_MAP[inv.invoice_status].label}
              </span>
            )}
            <span
              className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-0.5 ${photosCount > 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700/60 text-slate-400"}`}
              title={`${photosCount} document(s) joint(s)`}
              data-testid={`card-photos-${inv.id}`}
            >
              <Paperclip className="w-2.5 h-2.5" />{photosCount}
            </span>
          </div>
          {inv.paid_amount > 0 && inv.paid_amount < inv.amount && (
            <p className="text-[9px] text-amber-400 mt-0.5">Payé: {formatFCFA(inv.paid_amount)} / {formatFCFA(inv.amount)}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {inv.pipeline_courier_name && (
          <span className="text-[9px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-full truncate max-w-[80px]">
            <Truck className="w-2.5 h-2.5 inline mr-0.5" />{inv.pipeline_courier_name}
          </span>
        )}
        <div className="flex-1" />
        <button onClick={() => onJoin(inv.id)} className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400" title="Joindre depuis Archives" data-testid={`join-archives-${inv.id}`}>
          <Paperclip className="w-3 h-3" />
        </button>
        {isAdmin && (
          <>
            <button onClick={() => onAssign(inv.id)} className="p-1 rounded hover:bg-blue-500/20 text-blue-400" title="Assigner agent commercial"><UserPlus className="w-3 h-3" /></button>
            <button onClick={() => onLiasse(inv.id)} className="p-1 rounded hover:bg-purple-500/20 text-purple-400" title="Envoyer liasse"><Mail className="w-3 h-3" /></button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Static overlay card during drag ─── */
function DragOverlayCard({ inv }) {
  if (!inv) return null;
  return (
    <div className="bg-slate-900 border-2 border-cyan-500 rounded-lg p-3 shadow-2xl shadow-cyan-500/20 w-56">
      <p className="text-xs font-bold text-white">{inv.invoice_number}</p>
      <p className="text-[10px] text-slate-400">{inv.pharmacy_name}</p>
      <p className="text-xs font-bold text-emerald-400 mt-1">{formatFCFA(inv.amount)}</p>
    </div>
  );
}

/* ─── Kanban Column ─── */
function KanbanColumn({ stage, invoices, color, onAssign, onLiasse, onJoin, isAdmin = true }) {
  const c = COLOR_MAP[color];
  const Icon = stage.icon;
  const total = invoices.reduce((s, inv) => s + (inv.amount || 0), 0);
  const { setNodeRef, isOver } = useDroppable({ id: `column-${stage.key}`, data: { stage: stage.key } });
  return (
    <div className={`flex flex-col min-w-[220px] max-w-[260px] rounded-xl border ${isOver ? "border-cyan-400 ring-2 ring-cyan-500/30" : c.border} ${c.bg} overflow-hidden transition-all`} data-testid={`column-${stage.key}`}>
      <div className="p-3 border-b border-white/5 flex items-center gap-2">
        <div className={`w-7 h-7 rounded-lg ${c.badge} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-white truncate">{stage.label}</p>
          <p className={`text-[10px] ${c.text}`}>{invoices.length} · {formatFCFA(total)}</p>
        </div>
      </div>
      <div ref={setNodeRef} className={`flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-340px)] min-h-[120px] ${isOver ? "bg-cyan-500/5" : ""}`} data-testid={`drop-${stage.key}`}>
        {invoices.length === 0 && (
          <div className="flex items-center justify-center py-8 text-slate-600 text-[10px]">Aucune facture</div>
        )}
        {invoices.map(inv => (
          <DraggableCard key={inv.id} inv={inv} onAssign={onAssign} onLiasse={onLiasse} onJoin={onJoin} isAdmin={isAdmin} />
        ))}
      </div>
    </div>
  );
}

/* ─── Payment Tracking Tab ─── */
function PaymentTrackingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/log/pipeline/payment-tracking`, { headers: getHeaders() });
        setData(res.data);
      } catch { toast.error("Erreur chargement suivi paiements"); }
      finally { setLoading(false); }
    })();
  }, []);

  const exportPDF = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/log/pipeline/payment-tracking/pdf`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "CMES_Suivi_Paiements.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF telecharge !");
    } catch { toast.error("Erreur export PDF"); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  if (!data || !data.insurers.length) return <div className="text-center py-16 text-slate-500"><p>Aucune donnée disponible</p></div>;

  return (
    <div className="space-y-4" data-testid="payment-tracking">
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" /> Suivi des paiements par assureur
            </h2>
            <p className="text-xs text-slate-500 mt-1">Delais de paiement et encours par compagnie d'assurance</p>
          </div>
          <button onClick={exportPDF} disabled={exporting} data-testid="export-tracking-pdf"
            className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded-lg transition disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            Export PDF
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tracking-table">
            <thead>
              <tr className="text-[10px] text-slate-500 uppercase border-b border-white/5 bg-slate-950">
                <th className="text-left px-4 py-3">Assureur</th>
                <th className="text-center px-4 py-3">Factures</th>
                <th className="text-right px-4 py-3">Montant total</th>
                <th className="text-right px-4 py-3">Payé</th>
                <th className="text-right px-4 py-3">Encours</th>
                <th className="text-center px-4 py-3">Taux</th>
                <th className="text-center px-4 py-3">Délai moy.</th>
                <th className="text-center px-4 py-3">En retard</th>
                <th className="text-center px-4 py-3">Critique</th>
                <th className="text-center px-4 py-3">Détails</th>
              </tr>
            </thead>
            <tbody>
              {data.insurers.map(ins => (
                <tr key={ins.insurer_name} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${expanded === ins.insurer_name ? "bg-cyan-500/5" : ""}`}>
                  <td className="px-4 py-3"><p className="text-white font-semibold text-xs">{ins.insurer_name}</p></td>
                  <td className="px-4 py-3 text-center"><span className="text-white font-bold text-xs">{ins.total_invoices}</span></td>
                  <td className="px-4 py-3 text-right"><span className="text-white text-xs">{formatFCFA(ins.total_amount)}</span></td>
                  <td className="px-4 py-3 text-right"><span className="text-emerald-400 text-xs font-bold">{formatFCFA(ins.total_paid)}</span></td>
                  <td className="px-4 py-3 text-right"><span className="text-amber-400 text-xs font-bold">{formatFCFA(ins.total_remaining)}</span></td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-bold ${ins.recovery_rate >= 80 ? "text-emerald-400" : ins.recovery_rate >= 50 ? "text-amber-400" : "text-red-400"}`}>
                      {ins.recovery_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      ins.avg_delay_days > 90 ? "bg-red-500/10 text-red-400 border border-red-500/30" :
                      ins.avg_delay_days > 45 ? "bg-amber-500/10 text-amber-400 border border-amber-500/30" :
                      "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                    }`}>
                      <Clock className="w-3 h-3" /> {ins.avg_delay_days}j
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {ins.overdue_count > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/30">
                        <AlertTriangle className="w-3 h-3" /> {ins.overdue_count}
                      </span>
                    ) : <span className="text-slate-600 text-[10px]">0</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {ins.critical_count > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/30">
                        <AlertTriangle className="w-3 h-3" /> {ins.critical_count}
                      </span>
                    ) : <span className="text-slate-600 text-[10px]">0</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => setExpanded(expanded === ins.insurer_name ? null : ins.insurer_name)}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-cyan-400 transition"
                      data-testid={`tracking-expand-${ins.insurer_name}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expanded insurer detail */}
      {expanded && data.insurers.find(i => i.insurer_name === expanded) && (() => {
        const ins = data.insurers.find(i => i.insurer_name === expanded);
        return (
          <div className="bg-slate-900 border border-cyan-500/20 rounded-xl p-5 space-y-3" data-testid="tracking-detail">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{ins.insurer_name} — Détail des factures en cours</h3>
              <button onClick={() => setExpanded(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] text-slate-500 uppercase border-b border-white/5">
                    <th className="text-left px-3 py-2">N° Facture</th>
                    <th className="text-left px-3 py-2">Pharmacie</th>
                    <th className="text-right px-3 py-2">Montant</th>
                    <th className="text-right px-3 py-2">Payé</th>
                    <th className="text-right px-3 py-2">Encours</th>
                    <th className="text-center px-3 py-2">Statut facture</th>
                    <th className="text-center px-3 py-2">Étape pipeline</th>
                    <th className="text-center px-3 py-2">Délai</th>
                  </tr>
                </thead>
                <tbody>
                  {ins.invoices.map(inv => {
                    const stage = STAGES.find(s => s.key === inv.pipeline_stage) || STAGES[0];
                    const clr = COLOR_MAP[stage.color];
                    const invStatus = INVOICE_STATUS_MAP[inv.invoice_status] || { label: inv.invoice_status || "—", color: "bg-slate-500/20 text-slate-400" };
                    return (
                      <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-3 py-2 text-white font-medium">{inv.invoice_number}</td>
                        <td className="px-3 py-2 text-slate-400">{inv.pharmacy_name}</td>
                        <td className="px-3 py-2 text-right text-white">{formatFCFA(inv.amount)}</td>
                        <td className="px-3 py-2 text-right text-emerald-400">{formatFCFA(inv.paid_amount || 0)}</td>
                        <td className="px-3 py-2 text-right text-amber-400 font-bold">{formatFCFA(inv.remaining)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${invStatus.color}`}>
                            {invStatus.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${clr.bg} ${clr.text} border ${clr.border}`}>
                            {stage.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`font-bold ${inv.delay_days > 90 ? "text-red-400" : inv.delay_days > 45 ? "text-amber-400" : "text-slate-400"}`}>
                            {inv.delay_days}j
                            {inv.delay_status === "critical" && <span className="ml-1 text-[9px] text-red-400">!</span>}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ─── Main Pipeline Page ─── */
export default function LogPipelinePage() {
  const storedUser = getStoredUser();
  const navigate = useNavigate();
  const isAdmin = storedUser?.role === "superadmin" || storedUser?.role === "admin";
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("");
  const [insurerFilter, setInsurerFilter] = useState("");
  const [pharmacies, setPharmacies] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [assignData, setAssignData] = useState({ courier_id: "", courier_name: "", mission_type: "", note: "" });
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("kanban");
  const [activeDragId, setActiveDragId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (searchTerm) params.set("search", searchTerm);
      if (pharmacyFilter) params.set("pharmacy_id", pharmacyFilter);
      if (insurerFilter) params.set("insurer_name", insurerFilter);

      const [invRes, dashRes, pharmRes, insRes, courRes] = await Promise.all([
        axios.get(`${API}/log/pipeline/invoices?${params}`, { headers: getHeaders() }),
        axios.get(`${API}/log/pipeline/dashboard`, { headers: getHeaders() }),
        axios.get(`${API}/log/pipeline/pharmacies`, { headers: getHeaders() }),
        axios.get(`${API}/log/pipeline/insurers`, { headers: getHeaders() }),
        axios.get(`${API}/log/courses/couriers/list`, { headers: getHeaders() }),
      ]);
      setInvoices(invRes.data.invoices || []);
      setDashboard(dashRes.data);
      setPharmacies(pharmRes.data.pharmacies || []);
      setInsurers(insRes.data.insurers || []);
      setCouriers(courRes.data.couriers || []);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [searchTerm, pharmacyFilter, insurerFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const invoicesByStage = useMemo(() => {
    const map = {};
    STAGES.forEach(s => { map[s.key] = []; });
    invoices.forEach(inv => {
      const stage = inv.pipeline_stage || "a_deposer";
      if (map[stage]) map[stage].push(inv);
      else map.a_deposer.push(inv);
    });
    return map;
  }, [invoices]);

  const handleDragStart = (event) => { setActiveDragId(event.active.id); };

  const handleDragEnd = async (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const invoiceId = active.id;
    const inv = invoices.find(i => i.id === invoiceId);
    if (!inv) return;

    // Find target stage from droppable column or from over data
    let targetStage = null;
    if (over.data?.current?.stage) {
      targetStage = over.data.current.stage;
    } else {
      // Check if over.id matches column-{stage.key}
      for (const stage of STAGES) {
        if (over.id === `column-${stage.key}`) {
          targetStage = stage.key;
          break;
        }
      }
    }

    if (!targetStage || targetStage === inv.pipeline_stage) return;

    // Validate: can only move forward
    const currentIdx = STAGES.findIndex(s => s.key === inv.pipeline_stage);
    const targetIdx = STAGES.findIndex(s => s.key === targetStage);
    if (targetIdx <= currentIdx) {
      toast.error("Impossible de reculer une facture dans le pipeline");
      return;
    }

    // Optimistic UI update
    setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, pipeline_stage: targetStage } : i));

    try {
      const body = { stage: targetStage, note: "Déplacé via drag-and-drop" };
      if (targetStage === "cheque_a_recuperer") body.payment_method = "cheque";
      if (targetStage === "bordereau_a_recuperer") body.payment_method = "virement";
      await axios.put(`${API}/log/pipeline/invoices/${invoiceId}/stage`, body, { headers: getHeaders() });
      toast.success(`Facture ${inv.invoice_number} → ${STAGES.find(s => s.key === targetStage)?.label}`);
      loadAll();
    } catch {
      toast.error("Erreur mise à jour");
      setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, pipeline_stage: inv.pipeline_stage } : i));
    }
  };

  const handleAssignCourier = async () => {
    if (!showAssignModal || !assignData.courier_id || !assignData.mission_type) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/log/pipeline/invoices/${showAssignModal}/assign-courier`, assignData, { headers: getHeaders() });
      toast.success("Agent Commercial assigné !");
      setShowAssignModal(null);
      setAssignData({ courier_id: "", courier_name: "", mission_type: "", note: "" });
      loadAll();
    } catch { toast.error("Erreur assignation"); }
    finally { setActionLoading(false); }
  };

  const handleSendLiasse = async (invoiceId) => {
    try {
      await axios.post(`${API}/log/pipeline/invoices/${invoiceId}/send-liasse`, {}, { headers: getHeaders() });
      toast.success("Liasse envoyée !");
    } catch { toast.error("Erreur envoi"); }
  };

  const draggedInvoice = activeDragId ? invoices.find(i => i.id === activeDragId) : null;

  return (
    <LogLayout>
      <SectionTabs tabs={INVOICES_TABS} />
      <div className="space-y-5" data-testid="pipeline-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="pipeline-title">{isAdmin ? "Pipeline Global des Factures" : "Pipeline de mes Factures"}</h1>
            <p className="text-sm text-slate-400 mt-1">{isAdmin ? "Glissez les factures entre les colonnes pour avancer leur étape" : "Suivez l'avancement de vos factures dans le cycle logistique"}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-800 rounded-lg p-0.5 border border-white/10">
              <button onClick={() => setActiveTab("kanban")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeTab === "kanban" ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-white"}`} data-testid="tab-kanban">
                <LayoutGrid className="w-3.5 h-3.5 inline mr-1" />Kanban
              </button>
              <button onClick={() => setActiveTab("tracking")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeTab === "tracking" ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-white"}`} data-testid="tab-tracking">
                <Table2 className="w-3.5 h-3.5 inline mr-1" />Suivi paiements
              </button>
            </div>
            <Button onClick={loadAll} className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2" size="sm" data-testid="pipeline-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary bar */}
        {dashboard && (
          <div className="flex flex-wrap gap-4 bg-slate-900 border border-white/10 rounded-xl p-3">
            <div className="flex items-center gap-2"><Package className="w-4 h-4 text-cyan-400" /><span className="text-xs text-slate-400">Total:</span><span className="text-xs font-bold text-white">{dashboard.total_invoices}</span></div>
            <div className="flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-400" /><span className="text-xs text-slate-400">Montant:</span><span className="text-xs font-bold text-emerald-400">{formatFCFA(dashboard.total_amount)}</span></div>
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-amber-400" /><span className="text-xs text-slate-400">Courses:</span><span className="text-xs font-bold text-amber-400">{dashboard.action_needed}</span></div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /><span className="text-xs text-slate-400">Pharmacies:</span><span className="text-xs font-bold text-blue-400">{dashboard.pharmacy_count}</span></div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 bg-slate-900 border border-white/10 rounded-xl p-3">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher..." className="bg-transparent border-none text-white placeholder:text-slate-500 focus-visible:ring-0 h-8 text-xs" data-testid="pipeline-search" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            {isAdmin && (
              <select value={pharmacyFilter} onChange={e => setPharmacyFilter(e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" data-testid="pipeline-filter-pharmacy">
                <option value="">Toutes pharmacies</option>
                {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <select value={insurerFilter} onChange={e => setInsurerFilter(e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" data-testid="pipeline-filter-insurer">
              <option value="">Tous assureurs</option>
              {insurers.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
            </select>
            {(searchTerm || pharmacyFilter || insurerFilter) && (
              <button onClick={() => { setSearchTerm(""); setPharmacyFilter(""); setInsurerFilter(""); }} className="text-red-400 hover:text-red-300 p-1"><X className="w-4 h-4" /></button>
            )}
          </div>
        </div>

        {/* Content */}
        {activeTab === "kanban" ? (
          loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
                {STAGES.map(stage => (
                  <KanbanColumn key={stage.key} stage={stage} color={stage.color}
                    invoices={invoicesByStage[stage.key] || []}
                    isAdmin={isAdmin}
                    onAssign={(id) => { setShowAssignModal(id); setAssignData({ courier_id: "", courier_name: "", mission_type: "", note: "" }); }}
                    onJoin={(id) => navigate(`/log/invoices?open=${id}&action=archives`)}
                    onLiasse={handleSendLiasse} />
                ))}
              </div>
              <DragOverlay>{draggedInvoice && <DragOverlayCard inv={draggedInvoice} />}</DragOverlay>
            </DndContext>
          )
        ) : (
          <PaymentTrackingTab />
        )}

        {/* Assign Courier Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="assign-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Truck className="w-5 h-5 text-cyan-400" /> Assigner un agent commercial</h3>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Agent Commercial</label>
                <select value={assignData.courier_id}
                  onChange={e => { const c = couriers.find(cr => cr.id === e.target.value); setAssignData(d => ({ ...d, courier_id: e.target.value, courier_name: c ? c.full_name : "" })); }}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="assign-courier-select">
                  <option value="">Sélectionner...</option>
                  {couriers.map(c => <option key={c.id} value={c.id}>{c.full_name || c.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Type de mission</label>
                <select value={assignData.mission_type} onChange={e => setAssignData(d => ({ ...d, mission_type: e.target.value }))}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="assign-mission-select">
                  <option value="">Sélectionner...</option>
                  {MISSION_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Note (optionnel)</label>
                <Input value={assignData.note} onChange={e => setAssignData(d => ({ ...d, note: e.target.value }))} placeholder="Ex: Passer avant 14h" className="bg-slate-800 border-white/10 text-white" />
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="ghost" onClick={() => setShowAssignModal(null)} className="text-slate-400">Annuler</Button>
                <Button onClick={handleAssignCourier} disabled={!assignData.courier_id || !assignData.mission_type || actionLoading}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2" data-testid="assign-confirm">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Assigner
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}
